import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Modal, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ReleaseNoteEntry } from '../../../dto/releaseNotes';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { fetchReleaseNotes } from '../api/releaseNotesApi';
import { groupReleaseNotesByDate, splitReleaseNoteText } from '../releaseNotes';
import './ReleaseNotesVersionButton.css';

type LoadState =
  | { status: 'idle' | 'loading' | 'error' }
  | { status: 'loaded'; entries: ReleaseNoteEntry[] };

function ReleaseNoteText({ text }: { text: string }) {
  return (
    <>
      {splitReleaseNoteText(text).map((segment, index) => (
        segment.bold
          ? <strong key={index}>{segment.text}</strong>
          : <React.Fragment key={index}>{segment.text}</React.Fragment>
      ))}
    </>
  );
}

interface ReleaseNotesVersionButtonProps {
  /** Version label already shown in the sidebar, e.g. `v3.17.0`. */
  version: string;
}

/**
 * The discreet version number at the bottom of the sidebar; a click opens
 * „Co nowego” (business changelog, every role). Loads only when opened.
 */
export default function ReleaseNotesVersionButton({ version }: ReleaseNotesVersionButtonProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<LoadState>({ status: 'idle' });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      setState({ status: 'loaded', entries: await fetchReleaseNotes() });
    } catch {
      setState({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    if (open && (state.status === 'idle' || state.status === 'error')) {
      void load();
    }
    // Only an opening triggers a (re)load; a failed load retries on the next opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  let body: React.ReactNode;
  if (state.status === 'loaded') {
    const days = groupReleaseNotesByDate(state.entries);
    body = days.length === 0
      ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('releaseNotes.empty')} />
      : (
        <div className="release-notes__days">
          {days.map((day) => (
            <section key={day.date} className="release-notes__day" aria-label={formatDateEuropean(day.date)}>
              <h3 className="release-notes__date">{formatDateEuropean(day.date)}</h3>
              <ul className="release-notes__items">
                {day.items.map((item) => (
                  <li key={item.key}><ReleaseNoteText text={item.text} /></li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      );
  } else if (state.status === 'error') {
    body = (
      <Alert
        type="error"
        showIcon
        message={t('releaseNotes.loadError')}
        action={<Button size="small" onClick={() => void load()}>{t('retry')}</Button>}
      />
    );
  } else {
    body = (
      <div className="release-notes__loading">
        <Spin />
        <span>{t('releaseNotes.loading')}</span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="release-notes__version"
        aria-label={t('releaseNotes.openLabel', { version })}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {version}
      </button>
      <Modal
        open={open}
        title={t('releaseNotes.title')}
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>{t('close')}</Button>}
        width={640}
        className="release-notes"
      >
        <p className="release-notes__subtitle">
          {t('releaseNotes.subtitle', { version })} {t('releaseNotes.roleHint')}
        </p>
        {body}
      </Modal>
    </>
  );
}
